import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { BiCheckCircle } from "react-icons/bi";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { getGradeList } from "../../actions/Grade.action";
import {
  clearPaymentDue,
  getDuePayments,
} from "../../actions/Payment.action";
import { months, years } from "../../constants/MonthsAndYears";

const cellStyle = {
  padding: "0.5rem",
  verticalAlign: "middle",
};

const centeredCellStyle = {
  ...cellStyle,
  textAlign: "center",
};

const numberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const formatAmount = (value) => {
  const amount = parseFloat(value);
  return numberFormatter.format(Number.isNaN(amount) ? 0 : amount);
};

const getDueAmount = (payment) =>
  firstValue(payment.due_amount, payment.dueAmount, 0);

const getPaymentNote = (payment) =>
  firstValue(payment.note, payment.meta?.note, "-");

const getPaymentId = (payment) =>
  firstValue(payment.id, payment.paymentId, payment.payment_id);

const getStudentName = (payment) =>
  firstValue(
    payment.studentName,
    payment.name,
    payment.student?.name,
    payment.meta?.studentName,
    "Student"
  );

const getPaymentRoll = (payment) =>
  firstValue(
    payment.uid,
    payment.roll,
    payment.studentUid,
    payment.student?.uid,
    payment.student?.roll,
    payment.meta?.studentUid,
    "-"
  );

const getPaymentPhone = (payment) =>
  firstValue(
    payment.phone,
    payment.mobile,
    payment.studentPhone,
    payment.student?.phone,
    payment.meta?.studentPhone,
    "-"
  );

const getExamFee = (payment) =>
  firstValue(payment.exam_fee, payment.examFee, payment.admission_fee, 0);

const getExtraAmount = (payment) =>
  firstValue(
    payment.extra_amount,
    payment.extraAmount,
    payment.session_charge,
    payment.sessionCharge,
    0
  );

const getServiceCharge = (payment) =>
  firstValue(payment.amount, payment.service_charge, payment.serviceCharge, 0);

const getTotalAmount = (payment) => {
  const existingTotal = firstValue(payment.total_amount, payment.totalAmount);
  if (existingTotal !== undefined) {
    return existingTotal;
  }

  return (
    (parseFloat(getExamFee(payment)) || 0) +
    (parseFloat(getExtraAmount(payment)) || 0) +
    (parseFloat(getServiceCharge(payment)) || 0)
  );
};

const DueStudents = ({ grades, getGradeList }) => {
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(months[new Date().getMonth()]);
  const [duePayments, setDuePayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearingId, setClearingId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    if (grades === null) {
      getGradeList();
    }
  }, [grades, getGradeList]);

  const currentGrade = useMemo(
    () => grades?.find((item) => item.id === parseInt(grade)),
    [grades, grade]
  );
  const currentShifts = currentGrade?.shifts || [];
  const currentBatches =
    currentGrade?.batches?.filter((item) => item.shiftId === parseInt(shift)) ||
    [];

  const selectHandler = async () => {
    if (!year || !month) {
      toast.error("Please select year and month");
      return;
    }

    setLoading(true);
    const payments = await getDuePayments({
      tenant: "primary",
      year,
      month,
      gradeId: grade,
      shiftId: shift,
      batchId: batch,
    });
    setDuePayments(payments);
    setLoading(false);
  };

  const handleClearDue = async () => {
    if (!selectedPayment) return;

    const paymentId = getPaymentId(selectedPayment);
    if (!paymentId) {
      toast.error("Payment ID is missing");
      return;
    }

    setClearingId(paymentId);
    const result = await clearPaymentDue(paymentId);
    setClearingId(null);

    if (result.success) {
      setDuePayments((prev) =>
        prev.filter((payment) => {
          const currentPaymentId = getPaymentId(payment);
          if (currentPaymentId !== paymentId) {
            return true;
          }

          const clearedPayment = {
            ...payment,
            ...result.data,
            due: false,
            due_amount: result.data?.due_amount ?? 0,
          };

          return clearedPayment.due === true;
        })
      );
      setSelectedPayment(null);
      toast.success(result.message || "Due cleared successfully");
      return;
    }

    toast.error(result.error || "Failed to clear due");
  };

  const renderClearButton = (payment) => {
    const paymentId = getPaymentId(payment);
    const isClearing = clearingId === paymentId;

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={`clear-due-${paymentId}`}>Clear due</Tooltip>}
      >
        <span className="d-inline-block">
          <Button
            aria-label="Clear due"
            variant="outline-success"
            size="sm"
            onClick={() => setSelectedPayment(payment)}
            disabled={isClearing || !paymentId}
            style={{ width: 34, height: 34, padding: 0 }}
          >
            {isClearing ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <BiCheckCircle aria-hidden="true" size={20} />
            )}
          </Button>
        </span>
      </OverlayTrigger>
    );
  };

  const totalDueAmount = duePayments.reduce((sum, payment) => {
    return sum + (parseFloat(getDueAmount(payment)) || 0);
  }, 0);

  return (
    <Container className="pb-4">
      <Card bg="white" text="dark" className="crd shadow mb-4">
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="due-year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                onChange={(e) => setYear(parseInt(e.target.value))}
                id="due-year"
                name="year"
                value={year}
              >
                <option value={""}>Select Year</option>
                {years.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="due-month" className="d-block">
                  Month
                </label>
              </div>
              <Form.Select
                onChange={(e) => setMonth(e.target.value)}
                id="due-month"
                name="month"
                value={month}
              >
                <option value={""}>Select Month</option>
                {months.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="due-grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                id="due-grade"
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                }}
                value={grade}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="due-shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  id="due-shift"
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                  value={shift}
                >
                  <option value={""}>Select Shift</option>
                  {currentShifts.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="due-batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select
                  id="due-batch"
                  onChange={(e) => setBatch(e.target.value)}
                  value={batch}
                >
                  <option value={""}>Select Batch</option>
                  {currentBatches.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            <Col
              md={grade !== "" && shift !== "" ? 9 : 12}
              className="d-flex justify-content-end align-items-end py-3"
            >
              <Button
                onClick={selectHandler}
                variant="primary"
                type="button"
                className="btn_primary"
                disabled={loading}
              >
                {loading ? "Loading..." : "Select"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="crd p-md-4 pb-md-0 p-2">
        <Card.Body>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: 400 }}
            >
              <Spinner variant="dark" animation="grow" />
            </div>
          ) : duePayments.length === 0 ? (
            <span className="text-center fs-4 d-block pb-3">
              No due students found. Please select filters and click "Select".
            </span>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">
                    Due Students ({duePayments.length} students)
                  </h5>
                  <h6 className="text-muted mb-0">
                    {month} {year}
                  </h6>
                  <small className="text-muted">
                    Total Due: {formatAmount(totalDueAmount)}
                  </small>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  size="sm"
                  style={{ fontSize: "0.875rem" }}
                >
                  <thead>
                    <tr>
                      <th style={{ ...cellStyle, width: "6%" }}>Roll</th>
                      <th style={{ ...cellStyle, width: "16%" }}>Name</th>
                      <th style={{ ...cellStyle, width: "10%" }}>Phone</th>
                      <th style={{ ...cellStyle, width: "11%" }}>
                        Admission Fee/ Exam Fee
                      </th>
                      <th style={{ ...cellStyle, width: "11%" }}>
                        Session Charge/ Extra Cost
                      </th>
                      <th style={{ ...cellStyle, width: "10%" }}>
                        Service Charge
                      </th>
                      <th style={{ ...cellStyle, width: "9%" }}>
                        Due Amount
                      </th>
                      <th style={{ ...cellStyle, width: "8%" }}>Total</th>
                      <th style={{ ...cellStyle, width: "12%" }}>Note</th>
                      <th style={{ ...cellStyle, width: "8%" }}>Month</th>
                      <th style={{ ...cellStyle, width: "7%" }}>Year</th>
                      <th style={{ ...centeredCellStyle, width: "7%" }}>Due</th>
                      <th style={{ ...centeredCellStyle, width: "6%" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...duePayments]
                      .sort((a, b) =>
                        String(getPaymentRoll(a)).localeCompare(
                          String(getPaymentRoll(b)),
                          undefined,
                          { numeric: true }
                        )
                      )
                      .map((payment, index) => {
                        const paymentId = getPaymentId(payment) || index;
                        return (
                          <tr key={paymentId}>
                            <td style={cellStyle}>{getPaymentRoll(payment)}</td>
                            <td
                              style={{
                                ...cellStyle,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 180,
                              }}
                              title={getStudentName(payment)}
                            >
                              {getStudentName(payment)}
                            </td>
                            <td style={cellStyle}>{getPaymentPhone(payment)}</td>
                            <td style={cellStyle}>
                              {formatAmount(getExamFee(payment))}
                            </td>
                            <td style={cellStyle}>
                              {formatAmount(getExtraAmount(payment))}
                            </td>
                            <td style={cellStyle}>
                              {formatAmount(getServiceCharge(payment))}
                            </td>
                            <td style={cellStyle}>
                              {formatAmount(getDueAmount(payment))}
                            </td>
                            <td style={cellStyle}>
                              {formatAmount(getTotalAmount(payment))}
                            </td>
                            <td
                              style={{
                                ...cellStyle,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 180,
                              }}
                              title={getPaymentNote(payment)}
                            >
                              {getPaymentNote(payment)}
                            </td>
                            <td style={cellStyle}>
                              {firstValue(payment.month, "-")}
                            </td>
                            <td style={cellStyle}>
                              {firstValue(payment.year, "-")}
                            </td>
                            <td style={centeredCellStyle}>
                              <Badge bg="danger">Due</Badge>
                            </td>
                            <td style={centeredCellStyle}>
                              {renderClearButton(payment)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    <tr className="table-secondary fw-bold">
                      <td
                        colSpan={3}
                        style={{
                          ...cellStyle,
                          textAlign: "right",
                        }}
                      >
                        Total
                      </td>
                      <td style={cellStyle}>
                        {formatAmount(
                          duePayments.reduce(
                            (sum, payment) =>
                              sum + (parseFloat(getExamFee(payment)) || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td style={cellStyle}>
                        {formatAmount(
                          duePayments.reduce(
                            (sum, payment) =>
                              sum + (parseFloat(getExtraAmount(payment)) || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td style={cellStyle}>
                        {formatAmount(
                          duePayments.reduce(
                            (sum, payment) =>
                              sum +
                              (parseFloat(getServiceCharge(payment)) || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td style={cellStyle}>{formatAmount(totalDueAmount)}</td>
                      <td style={cellStyle}>
                        {formatAmount(
                          duePayments.reduce(
                            (sum, payment) =>
                              sum + (parseFloat(getTotalAmount(payment)) || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td colSpan={5} style={cellStyle} />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!selectedPayment} onHide={() => setSelectedPayment(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Clear Due</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPayment
            ? `Are you sure you want to clear due for ${getStudentName(
                selectedPayment
              )} for ${firstValue(selectedPayment.month, month)} ${firstValue(
                selectedPayment.year,
                year
              )}? This will clear the due flag and reset Due Amount to 0.`
            : ""}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setSelectedPayment(null)}
            disabled={clearingId !== null}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleClearDue}
            disabled={clearingId !== null}
          >
            {clearingId !== null ? "Clearing..." : "Clear Due"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

export default connect(mapStateToProps, { getGradeList })(DueStudents);
